/**
 * NEX-1165 — where a visit that had to pass through `/login` was going, so it lands there after
 * signing in. The new-request mail links to `/solicitudes?requestId=<id>`; without this the depósito
 * landed on the default route and had to find the request again.
 *
 * Carried as `/login?returnTo=<path>` (a query, not router state, so it survives a full reload of
 * the login page). Only this panel's own paths are honoured: anything that could leave the origin
 * (`//host`, `/\host`, `https://…`, or a control character the URL parser drops, as in
 * `/<tab>/host`, which resolves to `//host`) or loop back to the auth screens is ignored. The auth
 * screens are matched the way the router matches them: any case, with or without a trailing slash.
 */

const AUTH_PATHS = ['/login', '/registro'];
// Any origin works: it only tells a same-origin path from one the browser would resolve elsewhere.
const PROBE_ORIGIN = 'https://panel.invalid';
// eslint-disable-next-line no-control-regex -- the control characters are exactly what is rejected.
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/;

/** The path + query to return to after `/login`, or `null` when there is none worth keeping. */
export function safeReturnTo(value: string | null | undefined): string | null {
  // `//host` and `/\host` resolve to another origin, so the origin check below refuses them too.
  if (!value || !value.startsWith('/') || CONTROL_CHARACTERS.test(value)) return null;
  let url: URL;
  try {
    url = new URL(value, PROBE_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== PROBE_ORIGIN) return null;
  const pathname = url.pathname.replace(/\/+$/, '').toLowerCase();
  if (AUTH_PATHS.includes(pathname)) return null;
  return value;
}

/** The `/login` URL for a visit to `pathname` + `search` that has no session. */
export function loginPathFor(pathname: string, search: string): string {
  const returnTo = safeReturnTo(pathname + search);
  return returnTo && returnTo !== '/' ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login';
}
